import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Icon } from './icons';

/** Long enough not to be chatty, short enough that a new response feels immediate. */
const POLL_MS = 30_000;

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Polls a single integer while closed. The full list is only fetched once the panel is
  // actually open, so an idle tab costs one tiny request every half minute.
  const { data: count } = useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: () => api.notifications.unreadCount(),
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: false,
  });

  const { data: feed, isLoading } = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: () => api.notifications.list(30),
    enabled: open,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => api.notifications.markRead(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAll = useMutation({
    mutationFn: () => api.notifications.markAllRead(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const unread = count?.unreadCount ?? 0;

  return (
    <div className="relative" ref={wrap}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        // The count belongs in the accessible name, not only in the badge: a screen
        // reader user otherwise hears "Notifications" and learns nothing.
        aria-label={unread > 0
          ? `Notifications, ${unread} unread`
          : 'Notifications, none unread'}
        className="relative grid h-8 w-8 place-items-center rounded-md
                   text-[var(--ff-text-muted)] hover:bg-[var(--ff-surface-raised)]
                   hover:text-[var(--ff-text)]"
      >
        <Icon name="bell" size={17} />
        {unread > 0 && (
          <span
            aria-hidden
            className="absolute -right-0.5 -top-0.5 min-w-[16px] rounded-full
                       bg-[var(--ff-danger,#dc2626)] px-1 text-[10px] font-semibold
                       leading-4 text-white"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 z-30 mt-1 w-96 max-w-[calc(100vw-2rem)] rounded-md border
                     border-[var(--ff-border)] bg-[var(--ff-surface)] shadow-lg"
        >
          <div className="flex items-center justify-between border-b border-[var(--ff-border)] px-3 py-2">
            <h2 className="text-sm font-semibold">Notifications</h2>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => markAll.mutate()}
                disabled={markAll.isPending}
                className="text-xs text-[var(--ff-primary)] hover:underline"
              >
                Mark all read
              </button>
            )}
          </div>

          <ul className="max-h-96 overflow-auto">
            {isLoading && (
              <li className="px-3 py-6 text-center text-sm text-[var(--ff-text-muted)]">
                Loading…
              </li>
            )}

            {!isLoading && (feed?.data.length ?? 0) === 0 && (
              <li className="px-3 py-8 text-center text-sm text-[var(--ff-text-muted)]">
                Nothing yet. New responses to your forms will show up here.
              </li>
            )}

            {feed?.data.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  onClick={() => {
                    if (!n.readUtc) markRead.mutate(n.id);
                    if (n.url) {
                      setOpen(false);
                      // Notification URLs are produced by our own worker, so they are
                      // in-app paths and routed rather than sent through the browser.
                      navigate(n.url.replace(/^https?:\/\/[^/]+/, ''));
                    }
                  }}
                  className="flex w-full gap-3 border-b border-[var(--ff-border)] px-3 py-2.5
                             text-left last:border-0 hover:bg-[var(--ff-surface-raised)]"
                >
                  <span
                    aria-hidden
                    className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                      n.readUtc ? 'bg-transparent' : 'bg-[var(--ff-primary)]'
                    }`}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{n.title}</span>
                    {n.body && (
                      <span className="block truncate text-xs text-[var(--ff-text-muted)]">
                        {n.body}
                      </span>
                    )}
                    <span className="mt-0.5 block text-[11px] text-[var(--ff-text-muted)]">
                      {relativeTime(n.createdUtc)}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>

          <div className="border-t border-[var(--ff-border)] px-3 py-2">
            <PushOptIn />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Browser push is opt-in and additive: the feed above is the reliable channel, so a
 * denied permission or an unconfigured VAPID key costs nothing.
 */
function PushOptIn() {
  const [state, setState] = useState<'idle' | 'working' | 'on' | 'unavailable' | 'denied'>(
    () => (('Notification' in window && 'serviceWorker' in navigator) ? 'idle' : 'unavailable'),
  );

  const { data: key } = useQuery({
    queryKey: ['push', 'key'],
    queryFn: () => api.notifications.pushKey(),
    staleTime: Infinity,
  });

  if (state === 'unavailable' || !key?.publicKey) {
    return (
      <p className="text-xs text-[var(--ff-text-muted)]">
        {key?.publicKey
          ? 'This browser does not support push notifications.'
          : 'Browser push is not configured on this server.'}
      </p>
    );
  }

  if (state === 'on') {
    return <p className="text-xs text-[var(--ff-text-muted)]">Browser notifications are on.</p>;
  }

  if (state === 'denied') {
    return (
      <p className="text-xs text-[var(--ff-text-muted)]">
        Notifications are blocked for this site in your browser settings.
      </p>
    );
  }

  return (
    <button
      type="button"
      disabled={state === 'working'}
      onClick={async () => {
        setState('working');
        try {
          const permission = await Notification.requestPermission();
          if (permission !== 'granted') { setState('denied'); return; }

          const registration = await navigator.serviceWorker.register('/push-sw.js');
          const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(key.publicKey!),
          });

          const json = subscription.toJSON();
          await api.notifications.subscribe({
            endpoint: subscription.endpoint,
            p256dh: json.keys?.p256dh ?? '',
            auth: json.keys?.auth ?? '',
          });
          setState('on');
        } catch {
          setState('idle');
        }
      }}
      className="text-xs text-[var(--ff-primary)] hover:underline disabled:opacity-50"
    >
      {state === 'working' ? 'Enabling…' : 'Also notify me in this browser'}
    </button>
  );
}

/** VAPID keys are base64url; the subscribe API wants raw bytes. */
function urlBase64ToUint8Array(base64: string) {
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  const raw = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

function relativeTime(iso: string) {
  const then = new Date(iso).getTime();
  const seconds = Math.round((Date.now() - then) / 1000);

  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}
